<template>
  <div class="mobile-scroll-wrapper">
    <div class="task-report-page" :class="{ 'wxwork-mobile': isWxWorkMobile }">
      <!-- 标题适配：移动端更大更醒目 -->
      <h2 class="page-title">两横一竖工作任务报表</h2>

      <!-- 筛选栏：优化为按钮并排布局 -->
      <div class="filter-bar">
        <el-form :model="filterForm" class="filter-form" @submit.prevent="fetchReport">
          <div class="filter-main">
            <!-- 时间范围 -->
            <el-form-item label="时间范围" class="filter-item">
              <el-date-picker 
                v-model="filterForm.dateRange" 
                type="daterange" 
                value-format="yyyyMMdd"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                clearable
                class="unified-width"
                :picker-options="{
                  disabledDate(time) {
                    return time.getTime() > Date.now(); // 禁用未来日期
                  }
                }">
              </el-date-picker>
            </el-form-item>
            
            <!-- 完成情况 -->
            <el-form-item label="完成情况" class="filter-item">
              <el-select 
                v-model="filterForm.completeStatus" 
                placeholder="请选择完成情况"
                clearable
                class="unified-width"
                popper-class="mobile-select-popper">
                <el-option label="按时完成" value="按时完成"></el-option>
                <el-option label="超时完成" value="超时完成"></el-option>
                <el-option label="未完成" value="未完成"></el-option>
                <el-option label="驳回" value="驳回"></el-option>
              </el-select>
            </el-form-item>
          </div>
          
          <!-- 操作按钮：三按钮并排显示，适配PC/移动端 -->
          <el-form-item class="filter-btn-group">
            <el-button type="primary" @click="fetchReport" class="submit-btn" size="large" :loading="loading">查询</el-button>
            <el-button type="default" @click="resetFilter" class="reset-btn" size="large">重置</el-button>
            <el-button type="success" @click="downloadExcel" class="download-btn" size="large" :loading="loading">下载报表</el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 分页控件：移动端隐藏，改用加载更多 -->
      <div class="pagination-container" v-if="!isMobile && total > 0">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          background>
        </el-pagination>
      </div>

      <!-- PC端表格 -->
      <div class="pc-table" v-if="!isMobile">
        <el-table 
          :data="reportList" 
          border 
          stripe 
          style="width: 100%" 
          max-height="600"
          v-loading="loading"
          empty-text="暂无符合条件的报表数据">
          <el-table-column prop="assigneeName" label="完成人" width="120">
            <template slot-scope="scope">{{ scope.row.assigneeName || '未知' }}</template>
          </el-table-column>
          <el-table-column 
            prop="completeTime" 
            label="完成时间" 
            width="180"
            :formatter="formatTableDate">
          </el-table-column>
          <el-table-column prop="taskTitle" label="任务标题" min-width="200">
            <template slot-scope="scope">{{ scope.row.taskTitle || '无标题' }}</template>
          </el-table-column>
          <el-table-column prop="completeStatus" label="任务完成情况" width="150">
            <template slot-scope="scope">
              <el-tag :type="getStatusTagType(scope.row.completeStatus)">
                {{ scope.row.completeStatus || '未知状态' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="firstAuditResult" label="一级审核结果" min-width="220">
            <template slot-scope="scope">
              {{ scope.row.firstAuditResult || '无审核记录' }}
            </template>
          </el-table-column>
          <el-table-column prop="secondAuditResult" label="二级审核结果" min-width="220">
            <template slot-scope="scope">
              {{ scope.row.secondAuditResult || '无审核记录' }}
            </template>
          </el-table-column>
          <el-table-column prop="thirdAuditResult" label="三级审核结果" min-width="220">
            <template slot-scope="scope">
              {{ scope.row.thirdAuditResult || '无审核记录' }}
            </template>
          </el-table-column>
          <el-table-column prop="fourthAuditResult" label="四级审核结果" min-width="220">
            <template slot-scope="scope">
              {{ scope.row.fourthAuditResult || '无审核记录' }}
            </template>
          </el-table-column>
          <el-table-column prop="fifthAuditResult" label="五级审核结果" min-width="220">
            <template slot-scope="scope">
              {{ scope.row.fifthAuditResult || '无审核记录' }}
            </template>
          </el-table-column>
          <el-table-column 
            prop="score" 
            label="积分" 
            width="100" 
            align="center"
            :formatter="formatScore">
          </el-table-column>
        </el-table>
      </div>

      <!-- 手机端卡片：深度优化布局和交互 -->
      <div class="mobile-card-list" v-else>
        <!-- 加载状态 -->
        <div v-if="loading" class="mobile-loading">
          <el-skeleton :rows="3" animated />
        </div>
        
        <!-- 数据列表：优化间距、内边距、字体 -->
        <el-card 
          class="task-card" 
          v-for="item in reportList" 
          :key="item.taskId || item.id || Math.random()"
          shadow="always"
          v-loading="loading">
          <!-- 卡片头部：优化布局，突出核心信息 -->
          <div class="card-header">
            <div class="header-left">
              <span class="name">完成人：{{ item.assigneeName || '未知' }}</span>
              <span class="date">{{ formatCardDate(item.completeTime) }}</span>
            </div>
            <el-tag 
              :type="getStatusTagType(item.completeStatus)" 
              class="status-tag"
              size="medium">
              {{ item.completeStatus || '未知状态' }}
            </el-tag>
          </div>
          
          <!-- 卡片主体：优化字体大小和行高 -->
          <div class="card-body">
            <p class="title">任务标题：{{ item.taskTitle || '无标题' }}</p>
            
            <!-- 审核结果：折叠面板默认收起，优化触控区域 -->
            <el-collapse class="audit-collapse" border>
              <el-collapse-item 
                title="一级审核结果" 
                name="1"
                :disabled="!item.firstAuditResult">
                <p class="audit-content">{{ item.firstAuditResult || '无审核记录' }}</p>
              </el-collapse-item>
              <el-collapse-item 
                title="二级审核结果" 
                name="2"
                v-if="item.secondAuditResult">
                <p class="audit-content">{{ item.secondAuditResult }}</p>
              </el-collapse-item>
              <el-collapse-item 
                title="三级审核结果" 
                name="3"
                v-if="item.thirdAuditResult">
                <p class="audit-content">{{ item.thirdAuditResult }}</p>
              </el-collapse-item>
              <el-collapse-item 
                title="四级审核结果" 
                name="4"
                v-if="item.fourthAuditResult">
                <p class="audit-content">{{ item.fourthAuditResult }}</p>
              </el-collapse-item>
              <el-collapse-item 
                title="五级审核结果" 
                name="5"
                v-if="item.fifthAuditResult">
                <p class="audit-content">{{ item.fifthAuditResult }}</p>
              </el-collapse-item>
            </el-collapse>
          </div>
          
          <!-- 卡片底部：突出积分信息 -->
          <div class="card-footer">
            <span class="score">最终积分：{{ formatScore(null, item) }}分</span>
            <span v-if="item.completeStatus === '超时完成'" class="score-desc">（超时完成扣分一半）</span>
          </div>
        </el-card>
        
        <!-- 移动端加载更多：替代分页，增加边界判断 -->
        <div v-if="!loading && reportList.length > 0 && reportList.length < total" class="load-more">
          <el-button 
            type="text" 
            @click="loadMore"
            class="load-more-btn"
            :loading="loading">
            加载更多（已加载{{ reportList.length }}/{{ total }}）
          </el-button>
        </div>
        <!-- 无更多数据提示 -->
        <div v-if="!loading && reportList.length > 0 && reportList.length >= total" class="load-more no-more">
          <span>已加载全部{{ total }}条数据</span>
        </div>
      </div>

      <!-- 空数据提示：移动端优化样式 -->
      <div class="empty-tip" v-if="!loading && reportList.length === 0">
        <el-empty 
          description="暂无任务报表数据"
          image-size="80">
          <el-button type="primary" @click="resetFilter">重新查询</el-button>
        </el-empty>
      </div>
    </div>
  </div>
</template>

<script>
import { getTaskReport, downloadTaskExcel } from '@/api/report';
import { isWxWorkEnv } from '@/utils/request';

export default {
  name: 'TaskReport',
  data() {
    return {
      filterForm: {
        dateRange: [],
        completeStatus: ''
      },
      reportList: [],
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      isWxWorkMobile: false
    };
  },
  computed: {
    // 响应式判断：窗口宽度<768为移动端
    isMobile() {
      return window.innerWidth < 768;
    },
    // 登录用户ID：增加空值兜底
    loginWechatUserId() {
      const currentAdmin = localStorage.getItem('adminName');
      return currentAdmin || '';
    }
  },
  created() {
    // 企业微信移动端判断
    this.isWxWorkMobile = isWxWorkEnv() && /mobile/i.test(navigator.userAgent);
    // 监听窗口大小变化，更新响应式布局
    window.addEventListener('resize', this.handleResize);
  },
  destroyed() {
    // 销毁时移除事件监听，防止内存泄漏
    window.removeEventListener('resize', this.handleResize);
  },
  activated() {
    // 路由激活时校验登录态并查询数据
    this.checkLoginStatus();
  },
  mounted() {
    // 页面挂载时校验登录态并查询数据
    this.checkLoginStatus();
  },
  methods: {
    // 窗口大小变化时强制更新视图
    handleResize() {
      this.$forceUpdate();
    },

    // 登录态校验：未登录则跳转，已登录则查询数据
    checkLoginStatus() {
      if (!this.loginWechatUserId) {
        this.loading = false;
        this.$message?.warning('请先登录，即将跳转到登录页');
        setTimeout(() => {
          this.$router.push('/login');
        }, 1500);
        return false;
      }
      // 初始化查询数据
      this.fetchReport();
      return true;
    },

    // PC端分页：每页条数变化
    handleSizeChange(val) {
      this.pageSize = val;
      this.currentPage = 1; // 重置页码为1
      this.fetchReport();
    },
    // PC端分页：当前页码变化
    handleCurrentChange(val) {
      this.currentPage = val;
      this.fetchReport();
    },

    // 移动端加载更多：增加数据边界判断，防止重复请求
    loadMore() {
      // 防重复点击+数据总量校验
      if (this.loading || this.reportList.length >= this.total) return;
      this.currentPage += 1;
      this.fetchReport(true);
    },

    // 查询报表核心方法：支持追加数据（加载更多），优化容错和loading
    async fetchReport(append = false) {
      // 前置拦截：重复请求+未登录
      if (this.loading || !this.loginWechatUserId) {
        this.loading = false;
        return;
      }
      this.loading = true;

      try {
        // 构造请求参数，空值兜底
        const params = {
          loginWechatUserId: this.loginWechatUserId,
          startDate: this.filterForm.dateRange[0] || '',
          endDate: this.filterForm.dateRange[1] || '',
          completeStatus: this.filterForm.completeStatus || '',
          pageNum: this.currentPage,
          pageSize: this.pageSize
        };

        // 调用接口查询数据
        const res = await getTaskReport(params);
        // 接口返回数据兜底
        const responseData = res?.data || { records: [], total: 0 };
        const newList = Array.isArray(responseData.records) ? responseData.records : [];
        this.total = Number.isFinite(Number(responseData.total)) ? Number(responseData.total) : 0;

        // 处理数据：加载更多则追加，否则替换
        this.reportList = append ? [...this.reportList, ...newList] : newList;

        // 非加载更多时给出用户提示
        if (!append) {
          if (this.total > 0) {
            this.$message?.success(`查询成功，共${this.total}条数据`);
          } else {
            this.$message?.info('暂无符合条件的报表数据');
          }
        }
      } catch (error) {
        console.error('【任务报表】查询失败：', error);
        this.$message?.error('查询失败，请检查网络或稍后重试');
        // 加载更多失败时，页码回退
        if (append) this.currentPage--;
        // 首次查询失败时，清空数据
        else { this.reportList = []; this.total = 0; }
      } finally {
        // 无论成功失败，都关闭loading
        this.loading = false;
      }
    },

    // 下载Excel报表：优化企业微信兼容、文件名解析、错误处理
    async downloadExcel() {
      // 未登录拦截
      if (!this.loginWechatUserId) {
        this.$message?.warning('请先登录后再下载报表');
        return;
      }

      this.loading = true;
      try {
        // 构造下载参数（与查询参数一致，去除分页）
        const params = {
          loginWechatUserId: this.loginWechatUserId,
          startDate: this.filterForm.dateRange[0] || '',
          endDate: this.filterForm.dateRange[1] || '',
          completeStatus: this.filterForm.completeStatus || ''
        };

        // 调用下载接口，要求返回blob流
        const res = await downloadTaskExcel(params, {
          responseType: 'blob'
        });

        // 解析后端返回的文件名，解决乱码问题
        let fileName = '任务积分报表.xlsx';
        const contentDisposition = res.headers['content-disposition'] || res.headers['Content-Disposition'];
        if (contentDisposition) {
          // 标准化文件名解析：匹配filename*=UTF-8''xxx 或 filename=xxx
          const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.*)/);
          const normalMatch = contentDisposition.match(/filename=(.*)/);
          if (utf8Match && utf8Match[1]) {
            fileName = decodeURIComponent(utf8Match[1]);
          } else if (normalMatch && normalMatch[1]) {
            fileName = decodeURIComponent(normalMatch[1].replace(/"/g, ''));
          }
        }

        // 处理Blob文件，校验文件是否为空
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
        });
        // 空文件判断（小于100字节判定为空）
        if (blob.size < 100) {
          this.$message?.warning('暂无符合条件的报表数据，下载失败');
          return;
        }

        // 企业微信移动端专属处理
        if (this.isWxWorkMobile) {
          this.handleWxWorkFileDownload(blob, fileName);
        } else {
          // 通用端（PC/普通手机）下载逻辑
          this.handleNormalFileDownload(blob, fileName);
        }
      } catch (error) {
        console.error('【任务报表】下载失败：', error);
        // 精细化错误提示
        let errorMsg = '下载失败，请稍后重试';
        if (error.response) {
          const status = error.response.status;
          switch (status) {
            case 401:
              errorMsg = '登录态失效，请重新登录后下载';
              setTimeout(() => this.$router.push('/login'), 1500);
              break;
            case 500:
              errorMsg = '后端生成Excel出错，请联系管理员';
              break;
            case 404:
              errorMsg = '报表接口不存在，请检查配置';
              break;
            default:
              errorMsg = `下载失败：${status}错误`;
          }
        } else if (error.request) {
          errorMsg = '网络异常，请检查网络连接';
        } else {
          errorMsg = `下载失败：${error.message || '请求异常'}`;
        }
        this.$message?.error(errorMsg);
      } finally {
        this.loading = false;
      }
    },

    // 企业微信移动端文件下载/预览：优化降级逻辑
    handleWxWorkFileDownload(blob, fileName) {
      try {
        // 尝试使用企业微信JS-SDK下载
        if (window.wx && window.wx.downloadFile) {
          const blobUrl = window.URL.createObjectURL(blob);
          window.wx.downloadFile({
            url: blobUrl,
            success: () => {
              this.$message?.success(`报表下载成功：${fileName}`);
              // 及时释放临时链接，防止内存泄漏
              window.URL.revokeObjectURL(blobUrl);
            },
            fail: (err) => {
              console.error('【企业微信】JS-SDK下载失败，降级为预览：', err);
              this.openWxWorkFilePreview(blob, fileName);
            }
          });
        } else {
          // 无JS-SDK时直接预览
          this.openWxWorkFilePreview(blob, fileName);
        }
      } catch (e) {
        console.error('【企业微信】下载/预览异常：', e);
        this.$message?.warning(`企业微信暂不支持直接下载，可联系管理员获取报表`);
      }
    },

    // 通用端文件下载逻辑：兼容所有非企业微信环境
    handleNormalFileDownload(blob, fileName) {
      try {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        // 触发点击下载
        link.click();
        // 延迟清理，确保下载完成
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
        }, 2000);
        // 兜底：若未自动下载，用window.open打开
        setTimeout(() => {
          if (!document.querySelector(`a[href="${downloadUrl}"]`)) {
            window.open(downloadUrl, '_blank');
          }
        }, 1000);
        this.$message?.success(`报表导出成功！若未自动下载，请检查浏览器下载弹窗拦截`);
      } catch (e) {
        console.error('【通用端】下载失败：', e);
        this.$message?.error('文件下载失败，请手动保存');
      }
    },

    // 企业微信移动端文件预览（降级方案）
    openWxWorkFilePreview(blob, fileName) {
      try {
        const fileUrl = window.URL.createObjectURL(blob);
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = fileUrl;
        document.body.appendChild(iframe);
        // 延迟清理iframe和临时链接
        setTimeout(() => {
          document.body.removeChild(iframe);
          window.URL.revokeObjectURL(fileUrl);
        }, 5000);
        this.$message?.success(`报表已打开预览（${fileName}），请点击右上角「保存」下载`);
      } catch (e) {
        console.error('【企业微信】预览失败：', e);
        this.$message?.warning('无法预览文件，请联系管理员');
      }
    },

    // 重置筛选条件：清空表单，重置页码，重新查询
    resetFilter() {
      this.filterForm = {
        dateRange: [],
        completeStatus: ''
      };
      this.currentPage = 1;
      this.reportList = [];
      this.total = 0;
      this.fetchReport();
      this.$message?.info('筛选条件已重置，正在重新查询');
    },

    // 根据完成状态返回el-tag对应的类型
    getStatusTagType(status) {
      const statusMap = {
        '按时完成': 'success',
        '超时完成': 'warning',
        '未完成': 'info',
        '驳回': 'danger'
      };
      return statusMap[status] || 'primary';
    },

    // 格式化表格中的完成时间：兼容8位（yyyyMMdd）和14位（yyyyMMddHHmmss）格式
    formatTableDate(row) {
      if (!row || !row.completeTime) return '-';
      const timeStr = String(row.completeTime).trim();
      // 8位日期：yyyyMMdd → yyyy-MM-dd
      if (timeStr.length === 8) {
        return timeStr.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
      }
      // 14位日期时间：yyyyMMddHHmmss → yyyy-MM-dd HH:mm
      else if (timeStr.length >= 14) {
        return timeStr.replace(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1-$2-$3 $4:$5');
      }
      // 其他格式直接返回
      return timeStr;
    },

    // 格式化移动端卡片中的完成时间：仅显示日期
    formatCardDate(time) {
      if (!time) return '-';
      const formatted = this.formatTableDate({ completeTime: time });
      return formatted.split(' ')[0] || formatted;
    },

    // 格式化积分：保留1位小数，空值兜底为0.0
    formatScore(_, row) {
      if (!row) return 0.0;
      const score = Number(row.score);
      return isNaN(score) ? 0.0 : score.toFixed(1);
    }
  }
};
</script>

<style scoped>
/* 基础滚动容器：优化移动端滚动体验，防止卡顿 */
.mobile-scroll-wrapper {
  width: 100%;
  height: 100vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 60px;
  box-sizing: border-box;
  background-color: #f5f5f5;
}

/* 报表主容器 */
.task-report-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 15px;
  background-color: #fff;
  min-height: 100%;
  box-sizing: border-box;
}

/* 企业微信移动端深度适配：全屏显示，无间距 */
.wxwork-mobile {
  max-width: 100% !important;
  margin: 0 !important;
  padding: 10px !important;
  border: none !important;
}

/* 页面标题：PC/移动端差异化适配 */
.page-title {
  font-size: 20px;
  text-align: center;
  margin: 0 0 20px 0;
  color: #303133;
  font-weight: 600;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}
.wxwork-mobile .page-title {
  font-size: 18px;
  margin-bottom: 15px;
  padding-bottom: 8px;
}

/* 筛选栏：优化背景和内边距，提升视觉层次 */
.filter-bar {
  margin-bottom: 20px;
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}
.filter-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
}

/* 筛选项主容器：PC端横向，移动端竖向，自适应换行 */
.filter-main {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  align-items: flex-end;
  width: 100%;
}
.filter-item {
  margin-bottom: 0 !important;
  flex: 1;
  min-width: 200px;
}
.filter-item label {
  font-size: 15px;
  color: #606266;
  margin-bottom: 8px;
  display: block;
  font-weight: 500;
}
/* 统一筛选组件宽度 */
.unified-width {
  width: 100% !important;
  box-sizing: border-box;
  font-size: 15px !important;
}

/* 筛选按钮组：三按钮等宽并排，适配所有屏幕 */
.filter-btn-group {
  display: flex;
  gap: 10px;
  width: 100%;
  flex-wrap: nowrap;
  margin-bottom: 0 !important;
}
.submit-btn, .reset-btn, .download-btn {
  flex: 1;
  height: 48px !important;
  font-size: 16px !important;
  border-radius: 8px !important;
  white-space: nowrap;
}

/* PC端表格容器：处理横向滚动 */
.pc-table {
  overflow-x: auto;
  margin-top: 10px;
}
/* 分页控件：右对齐，增加背景色 */
.pagination-container {
  margin-top: 15px;
  text-align: right;
}

/* 移动端卡片列表：优化间距和阴影，提升质感 */
.mobile-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 10px;
  width: 100%;
}
.task-card {
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
  border: none !important;
}

/* 卡片头部：左右布局，突出核心信息 */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 10px;
}
.header-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}
.date {
  font-size: 13px;
  color: #909399;
}
.status-tag {
  font-size: 14px !important;
  padding: 4px 12px !important;
  border-radius: 6px !important;
}

/* 卡片主体：优化字体和行高，提升阅读体验 */
.card-body {
  margin-bottom: 10px;
}
.title {
  font-size: 15px;
  color: #333;
  margin-bottom: 12px;
  line-height: 1.4;
  margin: 0 0 12px 0;
}
/* 审核结果折叠面板：优化样式 */
.audit-collapse {
  --el-collapse-border-color: #f0f0f0;
  --el-collapse-header-text-color: #606266;
  --el-collapse-header-font-size: 15px;
  --el-collapse-content-padding: 8px 0 !important;
}
.audit-content {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  padding: 4px 0;
  margin: 0;
}

/* 卡片底部：突出积分，增加分隔线 */
.card-footer {
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
  margin-top: 8px;
}
.score {
  font-size: 16px;
  font-weight: 600;
  color: #ff6700;
}
.score-desc {
  font-size: 12px;
  color: #909399;
  margin-left: 8px;
}

/* 移动端加载更多：居中显示，优化样式 */
.load-more {
  text-align: center;
  padding: 15px 0;
  font-size: 15px;
}
.load-more-btn {
  font-size: 15px;
  color: #409eff;
}
/* 无更多数据提示：灰色文字 */
.no-more span {
  color: #909399;
  font-size: 14px;
}

/* 移动端加载骨架屏：增加内边距 */
.mobile-loading {
  padding: 20px;
  width: 100%;
}

/* 空数据提示：优化间距和位置 */
.empty-tip {
  margin-top: 30px;
  text-align: center;
  padding: 20px;
  width: 100%;
}

/* 移动端下拉选择器：适配宽度 */
.mobile-select-popper {
  width: 100% !important;
  max-width: 300px !important;
  box-sizing: border-box;
}

/* 响应式适配：768px以下移动端 */
@media (max-width: 768px) {
  .filter-main {
    flex-direction: column;
    align-items: stretch;
  }
  .filter-item {
    min-width: 100%;
  }
  .filter-btn-group {
    flex-wrap: wrap;
    gap: 8px;
  }
  .submit-btn, .reset-btn, .download-btn {
    flex: 1 0 30%;
    min-width: 80px;
    height: 44px !important;
    font-size: 15px !important;
    padding: 0 8px !important;
  }
  .task-report-page {
    max-width: 100%;
    padding: 10px;
  }
  .page-title {
    font-size: 18px;
  }
  .filter-bar {
    padding: 12px;
    margin-bottom: 15px;
  }
}

/* 小屏手机适配：375px以下 */
@media (max-width: 375px) {
  .submit-btn, .reset-btn, .download-btn {
    font-size: 14px !important;
    padding: 0 4px !important;
    height: 40px !important;
  }
  .name {
    font-size: 15px;
  }
  .title {
    font-size: 14px;
  }
  .score {
    font-size: 15px;
  }
  .page-title {
    font-size: 17px;
  }
}

/* 横屏适配：移动端横屏时优化筛选项布局 */
@media (orientation: landscape) and (max-width: 768px) {
  .filter-main {
    flex-direction: row;
    flex-wrap: wrap;
  }
  .filter-item {
    width: 48%;
    min-width: 48%;
  }
  .mobile-card-list {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px;
  }
  .task-card {
    width: calc(50% - 5px);
  }
}

/* 隐藏滚动条：优化移动端视觉体验 */
::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}
::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 2px;
}
::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 2px;
}
::-webkit-scrollbar-thumb:hover {
  background: #c0c4cc;
}
</style>